import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { 
  updateGenericStatus,
  setNewStatus,
  setNote,
  setScheduledTime,
  toggleForm,
  clearError,
  selectNewStatus,
  selectNote,
  selectScheduledTime,
  selectShowForm,
  selectLoading,
  selectError,
  selectSuccess
} from './store/genericUpdateSlice';
import './OnboardingViewModal.css';
import { OverviewModal } from './stages/OverviewModal';
import { SignedDateModal } from './stages/SignedDateModal';
import { KickoffModal } from './stages/KickoffModal';
import { LinkedToPracticeModal } from './stages/LinkedToPracticeModal';
import { RegistrationCompleteModal } from './stages/RegistrationCompleteModal';
import { PortRequestModal } from './stages/PortRequestModal';
import { PhonesModal } from './stages/PhonesModal';
import { TrainingKbModal } from './stages/TrainingKbModal';
import { TrainingWorkflowModal } from './stages/TrainingWorkflowModal';
import { GoLiveModal } from './stages/GoLiveModal';

import { 
  clearModalOnboarding,
  selectModalOnboarding,
  selectModalPractice,
  selectModalStage,
  setModalStage,
  selectShowModal
} from './store/onboardingSlice';

const OnboardingViewModal = () => {
  // const [loading, setLoading] = useState(true);
  // const [error, setError] = useState(null);
  const [mountNode, setMountNode] = useState(null);

  const dispatch = useAppDispatch();
  const modalOnboarding = useAppSelector(state => selectModalOnboarding(state));
  const modalPractice = useAppSelector(state => selectModalPractice(state));
  const modalStage = useAppSelector(state => selectModalStage(state));
  const showModal = useAppSelector(state => selectShowModal(state));

  

  useEffect(() => {
    setMountNode(document.getElementById("root") ?? document.body);
  }, []);


  const handleOverlayClick = (e) => {
    // Only close if clicking on the overlay itself, not on the modal content
    if (e.target === e.currentTarget) {
      dispatch(clearModalOnboarding());
    }
  };

  const handleCopyClick = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here if desired
      console.log(`${label} copied to clipboard:`, text);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  // Add ESC key listener when modal is open
  useEffect(() => {
    if (showModal) {
      const handleEscapeKey = (e) => {
        if (e.key === 'Escape') {
          dispatch(clearModalOnboarding());
        }
      };
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [showModal, dispatch]);

  if (!showModal || !modalOnboarding) {
    return null;
  }

  return createPortal(
    <div 
      aria-modal="true" 
      role="dialog"
      className={`onboarding-modal-overlay ${showModal ? "" : "hidden"}`}
      onClick={handleOverlayClick}
    >
      <div className="onboarding-modal-container">
        {/* Header with spans */}
        <div className="onboarding-modal-header">
          <h1 className="onboarding-modal-title">
            <span className="onboarding-modal-title-name">{modalOnboarding?.name || 'Loading...'}</span>
            <span className="onboarding-modal-title-separator">•</span>
            <span className="onboarding-modal-title-id">ID: {modalOnboarding?.id}</span>
            <span className="onboarding-modal-title-separator">•</span>
            <span 
              className="onboarding-modal-title-practice clickable-practice-id"
              onClick={() => handleCopyClick(modalPractice?.practiceId || modalOnboarding?.practiceId || '', 'Practice ID')}
              title="Click to copy Practice ID"
            >
              Practice: {modalPractice?.practiceId || modalOnboarding?.practiceId || 'Not set'}
            </span>
            {modalOnboarding?.organizationId && (
              <>
                <span className="onboarding-modal-title-separator">•</span>
                <span 
                  className="onboarding-modal-title-organization clickable-practice-id"
                  onClick={() => handleCopyClick(modalOnboarding.organizationId, 'Organization ID')}
                  title="Click to copy Organization ID"
                >
                  Org: {modalOnboarding.organizationId}
                </span>
              </>
            )}
          </h1>
        </div>
        
        <div className="onboarding-modal-content">
          {/* {loading && (
            <div className="onboarding-modal-loading">
              <div className="onboarding-modal-loading-text">Loading onboarding details...</div>
            </div>
          )}
          
          {error && (
            <div className="onboarding-modal-error">
              <div className="onboarding-modal-error-text">Error: {error}</div>
            </div>
          )} */}
          
          {/* {!loading && !error && ( */}
          <>
            {/* Tab Navigation */}
            <div className="onboarding-modal-tabs">
              <button className={`onboarding-modal-tab-button ${modalStage === 'overview' ? 'onboarding-modal-tab-button-active' : 'onboarding-modal-tab-button-inactive'}`} onClick={() => dispatch(setModalStage('overview'))}>Overview</button>
              <button className={`onboarding-modal-tab-button ${modalStage === 'signedDate' ? 'onboarding-modal-tab-button-active' : 'onboarding-modal-tab-button-inactive'}`} onClick={() => dispatch(setModalStage('signedDate'))}>Signed Date</button>
              <button className={`onboarding-modal-tab-button ${modalStage === 'kickoff' ? 'onboarding-modal-tab-button-active' : 'onboarding-modal-tab-button-inactive'}`} onClick={() => dispatch(setModalStage('kickoff'))}>Kickoff</button>
              <button className={`onboarding-modal-tab-button ${modalStage === 'isLinkedToPractice' ? 'onboarding-modal-tab-button-active' : 'onboarding-modal-tab-button-inactive'}`} onClick={() => dispatch(setModalStage('isLinkedToPractice'))}>Linked to Practice</button>
              <button className={`onboarding-modal-tab-button ${modalStage === 'registrationComplete' ? 'onboarding-modal-tab-button-active' : 'onboarding-modal-tab-button-inactive'}`} onClick={() => dispatch(setModalStage('registrationComplete'))}>Registration Complete</button>
              <button className={`onboarding-modal-tab-button ${modalStage === 'portRequest' ? 'onboarding-modal-tab-button-active' : 'onboarding-modal-tab-button-inactive'}`} onClick={() => dispatch(setModalStage('portRequest'))}>Port Request</button>
              <button className={`onboarding-modal-tab-button ${modalStage === 'phones' ? 'onboarding-modal-tab-button-active' : 'onboarding-modal-tab-button-inactive'}`} onClick={() => dispatch(setModalStage('phones'))}>Phones</button>
              <button className={`onboarding-modal-tab-button ${modalStage === 'trainingKb' ? 'onboarding-modal-tab-button-active' : 'onboarding-modal-tab-button-inactive'}`} onClick={() => dispatch(setModalStage('trainingKb'))}>Training KB</button>
              <button className={`onboarding-modal-tab-button ${modalStage === 'trainingWorkflow' ? 'onboarding-modal-tab-button-active' : 'onboarding-modal-tab-button-inactive'}`} onClick={() => dispatch(setModalStage('trainingWorkflow'))}>Training Workflow</button>
              <button className={`onboarding-modal-tab-button ${modalStage === 'goLive' ? 'onboarding-modal-tab-button-active' : 'onboarding-modal-tab-button-inactive'}`} onClick={() => dispatch(setModalStage('goLive'))}>Go Live</button>
            </div>

            {/* Tab Content */}
            <div className="onboarding-modal-tab-content">
              <div>
                {modalStage === 'overview' && <OverviewModal modalOnboarding={modalOnboarding} modalPractice={modalPractice} />}
                {modalStage === 'signedDate' && <SignedDateModal modalOnboarding={modalOnboarding} modalPractice={modalPractice} />}
                {modalStage === 'kickoff' && <KickoffModal modalOnboarding={modalOnboarding} modalPractice={modalPractice} />}
                {modalStage === 'isLinkedToPractice' && <LinkedToPracticeModal modalOnboarding={modalOnboarding} modalPractice={modalPractice} />}
                {modalStage === 'registrationComplete' && <RegistrationCompleteModal modalOnboarding={modalOnboarding} modalPractice={modalPractice} />}
                {modalStage === 'portRequest' && <PortRequestModal modalOnboarding={modalOnboarding} modalPractice={modalPractice} />}
                {modalStage === 'phones' && <PhonesModal modalOnboarding={modalOnboarding} modalPractice={modalPractice} />}
                {modalStage === 'trainingKb' && <TrainingKbModal modalOnboarding={modalOnboarding} modalPractice={modalPractice} />}
                {modalStage === 'trainingWorkflow' && <TrainingWorkflowModal modalOnboarding={modalOnboarding} modalPractice={modalPractice} />}
                {modalStage === 'goLive' && <GoLiveModal modalOnboarding={modalOnboarding} modalPractice={modalPractice} />}
              </div>
            </div>
          </>
          {/* )} */}
        </div>
      </div>
    </div>
    ,
    mountNode
  );
};

const GenericUpdateForm = () => {
  const dispatch = useAppDispatch();

  const onboarding = useAppSelector(selectModalOnboarding);
  const stage = useAppSelector(selectModalStage);
  
  // Redux state
  const newStatus = useAppSelector(selectNewStatus);
  const note = useAppSelector(selectNote);
  const scheduledTime = useAppSelector(selectScheduledTime);
  const showForm = useAppSelector(selectShowForm);
  const loading = useAppSelector(selectLoading);
  const error = useAppSelector(selectError);
  const success = useAppSelector(selectSuccess);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newStatus) {
      dispatch(clearError());
      return;
    }

    try {
      await dispatch(updateGenericStatus({
        onboardingId: onboarding.id,
        stage: stage,
        newStatus: newStatus,
        note: note,
        scheduledTime: scheduledTime
      })).unwrap();
      
      console.log("Successfully updated generic status");
      
    } catch (error) {
      console.error("Error updating generic status: ", error);
    }
  }

  const handleToggleForm = () => {
    dispatch(toggleForm());
  }

  const handleNewStatusChange = (e) => {
    dispatch(setNewStatus(e.target.value));
  }

  const handleNoteChange = (e) => {
    dispatch(setNote(e.target.value));
  }

  const handleScheduledTimeChange = (date) => {
    dispatch(setScheduledTime(date));
  }

  if (!showForm) {
    return (
      <div className="generic-update-form">
        <button onClick={handleToggleForm}>Update Status</button>
      </div>
    )
  }

  return (
    <div className="generic-update-form">
      <button onClick={handleToggleForm}>Back</button>
      
      {/* Error Message */}
      {error && (
        <div className="generic-update-error">
          {error}
        </div>
      )}
      
      {/* Success Message */}
      {success && (
        <div className="generic-update-success">
          Status updated successfully!
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div>
          <label htmlFor="newStatus">New Status:</label>
          <select 
            id="newStatus" 
            name="newStatus" 
            value={newStatus || ''}
            onChange={handleNewStatusChange}
            disabled={loading}
          >
            <option value="">Select a status...</option>
            <option value="on-hold">On Hold</option>
            <option value="needs-action">Needs Action</option>
            <option value="follow-up">Follow Up</option>
            <option value="blocked">Blocked</option>
            <option value="scheduled">Scheduled</option>
            <option value="pending">Pending Communication</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        
        <input type="hidden" name="trigger" value="user" />
        
        <div>
          <label htmlFor="note">Note:</label>
          <input 
            type="text" 
            id="note" 
            name="note" 
            placeholder="Add a note..." 
            value={note || ''}
            onChange={handleNoteChange}
            disabled={loading}
          />
        </div>
        
        {/* Show scheduled time field when "scheduled" is selected */}
        {newStatus === "scheduled" && (
          <div>
            <label htmlFor="scheduledTime">Scheduled Time:</label>
            <DatePicker
              id="scheduledTime"
              selected={scheduledTime}
              onChange={handleScheduledTimeChange}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="MMMM d, yyyy h:mm aa"
              placeholderText="Select date and time"
              isClearable
              className="scheduled-time-picker"
              disabled={loading}
            />
          </div>
        )}
        
        <button type="submit" disabled={loading || !newStatus}>
          {loading ? 'Updating...' : 'Update Status'}
        </button>
      </form>
    </div>
  )
}

export { OnboardingViewModal, GenericUpdateForm };